import React, { ReactElement, useEffect, useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useGetClinicSettingsConversationsChannelsWithStatusesQuery } from 'shared/types/graphql';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { ModalCloseButton } from '@televet/kibble-ui/build/components/Modal';
import {
  CModal,
  CModalBody,
  ModalContent,
  CModalFooter,
  CModalHeader,
  ModalOverlay,
} from '@televet/kibble-ui/build/chakra';
import { FeatureFlagName } from 'shared/enums';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { ChannelStatusInactivityUnits } from './AutomaticStatusChanges';
import sub from 'date-fns/sub';
import isBefore from 'date-fns/isBefore';

interface IConfirmAutomaticStatusChangeModalProps {
  isOpen: boolean;
  statusIds: string[];
  onConfirm: () => Promise<void>;
  onClose: () => void;
  inactivityDurationUnit: ChannelStatusInactivityUnits;
  inactivityDurationValue: number;
}

const ConfirmAutomaticStatusChangeModal = ({
  isOpen,
  statusIds,
  onConfirm,
  onClose,
  inactivityDurationUnit,
  inactivityDurationValue,
}: IConfirmAutomaticStatusChangeModalProps): ReactElement => {
  const { currentClinicId } = useClinicUser();
  const { isFeatureEnabled } = useFeatureFlag();
  const isSortConversationsByLastMessageEnabled = isFeatureEnabled(FeatureFlagName.SortConversationByLastMessage);

  const { data: channelData, refetch } = useGetClinicSettingsConversationsChannelsWithStatusesQuery({
    variables: {
      where: {
        clinicId: { equals: currentClinicId },
        channelStatusId: { in: statusIds },
      },
    },
    skip: !currentClinicId || !isOpen,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  // Subtract the inactivity duration from the current date and time
  const lastActivityDateBoundary = useMemo(() => {
    const now = new Date();
    return sub(now, { [inactivityDurationUnit.toLocaleLowerCase()]: inactivityDurationValue });
  }, [inactivityDurationUnit, inactivityDurationValue]);

  // Filter the list of channels in the desired statuses by last activity
  const channelCount = useMemo(() => {
    const filteredChannels = channelData?.findManyChannel?.filter((channel) => {
      const lastActivityDate = new Date(
        isSortConversationsByLastMessageEnabled
          ? channel?.lastMessage?.createdAt || channel.updatedAt
          : channel?.updatedAt,
      );

      return isBefore(lastActivityDate, lastActivityDateBoundary);
    });

    return filteredChannels?.length || 0;
  }, [channelData, isSortConversationsByLastMessageEnabled, lastActivityDateBoundary]);

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  return (
    <CModal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <CModalHeader>Confirm Changes</CModalHeader>
        <ModalCloseButton />
        <CModalBody>This rule will affect {channelCount.toString()} conversations</CModalBody>
        <CModalFooter justifyContent="space-between">
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>Confirm</Button>
        </CModalFooter>
      </ModalContent>
    </CModal>
  );
};

export default ConfirmAutomaticStatusChangeModal;
